import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import * as React from "react";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import type { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

import { cn } from "../../lib/utils";
import { Input } from "../ui/input";

interface NumberInputProps {
	inputClassName?: string;
	field: Omit<ControllerRenderProps<any, any>, "ref">;
	fieldState: ControllerFieldState;
	unit?: string;
	placeholder?: string;
	showValidationErrors?: boolean;
}

const errorClasses = cn(
	"border border-yellow-300 bg-yellow-50 text-yellow-700 placeholder:text-yellow-700/40",
	"dark:border-stealth-error-outline dark:text-stealth-error-text",
);

export function NumberInput({
	field,
	fieldState,
	inputClassName,
	unit,
	placeholder,
	showValidationErrors,
}: NumberInputProps) {
	// Track raw input value
	const [inputValue, setInputValue] = useState<string | null>(null);

	const displayValue = useMemo(() => {
		if (field.value === null || field.value === undefined) return "";
		return field.value.toString();
	}, [field.value]);

	useEffect(() => {
		setInputValue(null);
	}, [field.value]);

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const pattern = /^-?\d*\.?\d*$/;

		if (value === "" || pattern.test(value)) {
			setInputValue(value);
		}
	};

	const handleBlur = (e?: React.FocusEvent<HTMLInputElement>) => {
		// Check if the newly focused element or any ancestor has the data-skip-field-onchange attribute
		if (e) {
			const relatedTarget = e.relatedTarget as HTMLElement | null;
			if (relatedTarget) {
				let current: HTMLElement | null = relatedTarget;
				while (current) {
					if (current.hasAttribute("data-skip-field-onchange")) {
						field.onBlur();
						return;
					}
					current = current.parentElement;
				}
			}
		}

		if (inputValue !== null) {
			if (inputValue === "") {
				field.onChange(null);
			} else {
				const numValue = Number(inputValue);
				if (!isNaN(numValue)) {
					field.onChange(numValue);
				}
			}
		}

		setInputValue(null);

		field.onBlur();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowUp" || e.key === "ArrowDown") {
			e.preventDefault();

			handleBlur();

			const currentValue = field.value || 0;

			const increment = e.key === "ArrowUp" ? 1 : -1;
			const newValue = currentValue + increment;

			field.onChange(newValue);
			field.onBlur();
		}
	};

	const hasError = Boolean(fieldState.error);
	const errorMessage = fieldState.error?.message;

	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-center gap-2 relative">
				<Input
					{...field}
					type="text"
					inputMode="numeric"
					value={inputValue !== null ? inputValue : displayValue}
					onChange={handleChange}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className={cn({ [errorClasses]: hasError }, inputClassName)}
					aria-invalid={hasError}
					aria-describedby={hasError ? `${field.name}-error` : undefined}
				/>
				{hasError && (
					<InfoOutlineIcon
						className={cn(
							errorClasses,
							"bg-transparent border-0 !size-4 absolute right-11",
						)}
					/>
				)}

				{unit && <span className="text-xs text-current whitespace-nowrap">{unit}</span>}
			</div>

			{hasError && errorMessage && showValidationErrors && (
				<span id={`${field.name}-error`} className="text-xs text-red-500" role="alert">
					{errorMessage}
				</span>
			)}
		</div>
	);
}
